import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Mail, LogIn, User, Settings, ShoppingCart } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { themeConfig } = useTheme();
  const { count: cartCount } = useCart();
  const [themeImageUrl, setThemeImageUrl] = useState(null);
  
  const primary = themeConfig?.theme?.primary || '#003366';
  const secondary = themeConfig?.theme?.secondary || '#FFD700';

  // Carregar Imagem do Tema (mesmo campo usado no rodapé)
  useEffect(() => {
    let objectUrl = null;
    const loadThemeImage = async () => {
      if (themeConfig?.theme_image) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/themes/image/${themeConfig.theme_image}`);
          if (response.ok) {
            const blob = await response.blob();
            objectUrl = URL.createObjectURL(blob);
            setThemeImageUrl(objectUrl);
          }
        } catch (error) {
          console.error('Error loading theme image (header):', error);
        }
      } else {
        setThemeImageUrl(null);
      }
    };
    loadThemeImage();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [themeConfig?.theme_image]);

  // Verificar se cliente está logado
  useEffect(() => {
    const checkAuth = () => {
      const auth = localStorage.getItem('customerData');
      if (auth) {
        try {
          setCustomer(JSON.parse(auth));
        } catch (e) {
          setCustomer(null);
        }
      } else {
        setCustomer(null);
      }
    };
    
    checkAuth();
    // Re-verificar em cada mudança de rota
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  // Pegar primeiro nome do cliente
  const getFirstName = () => {
    if (!customer?.full_name) return '';
    return customer.full_name.split(' ')[0];
  };

  // Labels FIXOS em português - NÃO TRADUZIR
  const navLinks = [
    { path: '/', label: 'Início' },
    { path: '/pacotes', label: 'Pacotes' },
    { path: '/pacotes-personalizados', label: 'Pacotes Personalizados' },
    { path: '/sobre', label: 'Sobre Nós' },
    { path: '/politica-de-cancelamento', label: 'Termos' },
    { path: '/contato', label: 'Contato' }
  ];

  return (
    <nav 
      className="shadow-md fixed w-full top-0 z-50"
      style={{ backgroundColor: themeConfig?.theme?.headerBg || '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo with theme image overlaid on the right side */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="relative inline-block w-14 h-14 shrink-0">
              {themeImageUrl && (
                <img 
                  src={themeImageUrl}
                  alt=""
                  aria-hidden="true"
                  className="absolute pointer-events-none top-0"
                  style={{
                    height: '100%',
                    width: 'auto',
                    left: '50%',
                    objectFit: 'contain',
                    zIndex: 1
                  }}
                />
              )}
              <img 
                src="https://customer-assets.emergentagent.com/job_419069bb-d46c-413f-9dd8-09d0cbbf3735/artifacts/rwf0qmt2_LOGO.png" 
                alt="TÔ INDO ALI" 
                className="h-14 w-14 object-contain relative"
                style={{ zIndex: 2 }}
              />
            </div>
            <div className="flex flex-col relative" style={{ zIndex: 3 }}>
              <span className="font-bold text-xl text-gray-900">TÔ INDO ALI</span>
              <span className="text-xs text-gray-600">Turismo e Aventura</span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'nav-link-active'
                    : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Search Box, Login e Admin - Desktop e Tablet */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  const query = searchQuery.trim().toLowerCase();
                  // Se buscar por "perguntas", "dúvidas" ou "faq", redireciona para FAQ
                  if (query.includes('pergunta') || query.includes('dúvida') || query.includes('duvida') || query === 'faq') {
                    navigate('/contato#faq');
                    setSearchQuery('');
                    // Scroll para a seção de FAQ
                    setTimeout(() => {
                      const faqSection = document.getElementById('faq');
                      if (faqSection) {
                        faqSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  } else {
                    navigate(`/pacotes?busca=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchQuery('');
                  }
                }
              }}
              className="relative"
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar..."
                className="w-28 lg:w-48 pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-theme-primary"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>

            {/* Cart - entre busca e login */}
            <Link
              to="/carrinho"
              className="relative flex items-center text-gray-700 hover:text-theme-primary transition-colors"
              title="Meu Carrinho"
              data-testid="navbar-cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  data-testid="navbar-cart-count"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            {customer ? (
              <Link 
                to="/minha-conta" 
                className="flex items-center space-x-1 lg:space-x-2 nav-link-primary transition-colors font-medium"
              >
                <User className="w-4 h-4" />
                <span className="text-xs lg:text-sm hidden lg:inline">Olá, {getFirstName()}</span>
                <span className="text-xs lg:hidden">{getFirstName()}</span>
              </Link>
            ) : (
              <Link 
                to="/cliente/login" 
                className="flex items-center space-x-1 nav-link-primary transition-colors font-medium"
                title="Área do Cliente"
              >
                <LogIn className="w-4 h-4" />
                <span className="text-xs lg:text-sm">Entrar</span>
              </Link>
            )}
            <Link 
              to="/admin/login" 
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Admin"
            >
              <Settings className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Cart + Menu Button */}
          <div className="md:hidden flex items-center gap-1">
            <Link
              to="/carrinho"
              className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
              aria-label="Carrinho"
              data-testid="navbar-cart-button-mobile"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span
                  className="absolute top-0 right-0 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  data-testid="navbar-cart-count-mobile"
                >
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Abrir menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {/* Mobile Search */}
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  const query = searchQuery.trim().toLowerCase();
                  // Se buscar por "perguntas", "dúvidas" ou "faq", redireciona para FAQ
                  if (query.includes('pergunta') || query.includes('dúvida') || query.includes('duvida') || query === 'faq') {
                    navigate('/contato#faq');
                    setSearchQuery('');
                    setIsOpen(false);
                    // Scroll para a seção de FAQ
                    setTimeout(() => {
                      const faqSection = document.getElementById('faq');
                      if (faqSection) {
                        faqSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  } else {
                    navigate(`/pacotes?busca=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchQuery('');
                    setIsOpen(false);
                  }
                }
              }}
              className="relative mb-4"
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar pacotes..."
                className="w-full pl-3 pr-10 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent"
                style={{ fontSize: '16px' }}
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-theme-primary"
              >
                <Search className="w-5 h-5" />
              </button>
            </form>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'btn-primary-theme'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <a href="mailto:contato@toindoali.com.br" className="flex items-center space-x-2 text-gray-700 px-4 py-2">
                <Mail className="w-4 h-4" />
                <span className="text-sm">contato@toindoali.com.br</span>
              </a>
              {customer ? (
                <Link 
                  to="/minha-conta" 
                  onClick={() => setIsOpen(false)}
                  className="flex items-center space-x-2 text-blue-900 px-4 py-2 font-medium"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">Olá, {getFirstName()}</span>
                </Link>
              ) : (
                <Link 
                  to="/cliente/login" 
                  onClick={() => setIsOpen(false)}
                  className="flex items-center space-x-2 text-blue-900 px-4 py-2 font-medium"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="text-sm">Minha Conta</span>
                </Link>
              )}
              <Link 
                to="/admin/login" 
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-2 text-gray-400 px-4 py-2"
              >
                <Settings className="w-4 h-4" />
                <span className="text-sm text-xs">Admin</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
